import { gql } from '@apollo/client';
import { Link } from 'react-router';
import type { IconName } from '../components/Icon';

/**
 * The pieces both search surfaces need — the ⌘K palette and `/search`.
 *
 * These five things were copy-pasted between the two files, which meant every
 * change had to be made twice and the withheld wording had already drifted apart.
 * Type metadata, highlighting and the query document live here now; the surfaces
 * own only their own layout.
 */

// ── the wire ─────────────────────────────────────────────────────────────────

export interface Hit {
  entityType: string;
  entityId: string;
  title: string;
  subtitle?: string;
  route: string;
  /**
   * WHERE the match landed, server-computed: 6 exact title · 5 title as whole
   * words · 4 title mid-token · 3 body (env-var key names) · 2 subtitle ·
   * 1 token/phrase only · 0 fuzzy near-miss. See MATCH_TIER in inventory-core.
   *
   * This is the SAME value the server sorted by, which is the whole point — the
   * chip explaining a row and the row's position can no longer disagree. They did:
   * a fuzzy near-miss used to render a 0.98 relevance bar beside a "similar" label,
   * because the bar read the score and the label re-derived the reason from strings.
   */
  tier: number;
  matchedBody: string[];
  /** TRUE size of this type's result set, before limitPerType clamped the rows.
   *  Same on every row of a type — see SearchHit.typeTotal in the API. */
  typeTotal: number;
  /** Secret sensitivity; absent for every other type. Only ever present on a row
   *  the caller already passed the RBAC gate for — see SearchHit.level. */
  level?: string;
}

export interface SearchData {
  search: Hit[];
  searchWithheld: number;
}

export const SEARCH = gql`
  query GlobalSearch($q: String!, $limitPerType: Int!, $entityType: String) {
    search(q: $q, limitPerType: $limitPerType, entityType: $entityType) {
      entityType
      entityId
      title
      subtitle
      route
      tier
      matchedBody
      typeTotal
      level
    }
    # Same document, one round trip — see the searchWithheld resolver for why this
    # isn't a field on the hits.
    searchWithheld(q: $q)
  }
`;

// ── entity types ─────────────────────────────────────────────────────────────

export interface TypeMeta {
  type: string;
  /** Plural, for tabs, facets and group headers. */
  label: string;
  /** Singular, for the type column on a result card. */
  short: string;
  icon: IconName;
  /** CSS custom property carrying this type's categorical colour. */
  color: string;
}

/**
 * Fixed display order, from the design handoff: repo · cluster · workload ·
 * namespace · secret.
 *
 * Two deviations, both forced by what the index actually contains:
 *
 *  - **Workloads become Services.** Workload docs were removed from the index on
 *    2026-07-28 — 47% were exact duplicates and 650 of 653 routed to `/repos/:id`,
 *    so one query returned the same link fourteen times and buried the real hits.
 *    `searchScope.ts` already aliases `workload:` → `service`, so the prefix the
 *    handoff documents keeps working; the tab is just honest about what it holds.
 *  - **Catalog is appended.** The handoff's five types predate it, but the index
 *    emits catalog docs and a type with no tab would be unreachable from the
 *    facets.
 *
 * This order supersedes the previous repo-then-secret ordering (whose rationale
 * was that those are the two primary lookups). If secrets feel buried in use,
 * that earlier reasoning is the argument for moving them back up.
 */
export const TYPE_META: TypeMeta[] = [
  { type: 'repo', label: 'Repos', short: 'repo', icon: 'repo', color: 'var(--accent)' },
  { type: 'cluster', label: 'Clusters', short: 'cluster', icon: 'cluster', color: 'var(--green)' },
  { type: 'service', label: 'Services', short: 'service', icon: 'grid', color: 'var(--violet)' },
  { type: 'namespace', label: 'Namespaces', short: 'namespace', icon: 'catalog', color: 'var(--amber)' },
  { type: 'secret', label: 'Secrets', short: 'secret', icon: 'key', color: 'var(--bad)' },
  { type: 'catalog', label: 'Catalog', short: 'catalog', icon: 'docs', color: 'var(--muted)' },
];

export const metaFor = (type: string): TypeMeta =>
  TYPE_META.find((m) => m.type === type) ?? {
    type,
    label: type,
    short: type,
    icon: 'inbox',
    color: 'var(--muted)',
  };

// ── highlighting ─────────────────────────────────────────────────────────────

export const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Query → the literal fragments worth highlighting. Single chars are dropped:
 *  highlighting every "a" is noise, not a cue. */
export function highlightTokens(q: string): string[] {
  return [...new Set(q.toLowerCase().split(/[\s,]+/).filter((t) => t.length >= 2))];
}

/**
 * Render `text` with every occurrence of a query token wrapped in <mark>, so the
 * eye lands on WHY a row matched. Built as React nodes (never innerHTML) — the
 * text is user/DB data and must not be parsed as markup.
 *
 * Matching is a literal, case-insensitive substring pass. Search itself is
 * trigram/FTS and typo-tolerant, so a hit may legitimately contain no literal
 * match (e.g. a fuzzy or key-name-body match) — then the text renders unchanged
 * rather than guessing.
 */
export function Highlighted({ text, tokens }: { text: string; tokens: string[] }) {
  if (!text || !tokens.length) return <>{text}</>;
  // One capture group → split() alternates [plain, match, plain, match, …],
  // so odd indices are the matches (no regex lastIndex bookkeeping).
  const parts = text.split(new RegExp(`(${tokens.map(escapeRe).join('|')})`, 'gi'));
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <mark className="hit-mark" key={i}>
            {p}
          </mark>
        ) : (
          p
        ),
      )}
    </>
  );
}

// ── "why did this match?" ────────────────────────────────────────────────────

/** Where a hit's match landed. Also the "Matched in" facet's buckets. */
export type MatchKind = 'name' | 'body' | 'context' | 'similar';

/** Human wording for a `tier`, for the tooltip on the tier pips. */
export const TIER_LABEL: Record<number, string> = {
  6: 'exact name match',
  5: 'name contains the term as whole words',
  4: 'name contains the term inside a longer word',
  3: 'a key / env-var name matched',
  2: 'context matched (subtitle)',
  1: 'all words present, not adjacent',
  0: 'similar spelling only — the term appears nowhere in this row',
};
/** Pip positions, highest tier first. Tier 0 lights none. */
export const TIER_PIPS = [6, 5, 4, 3, 2, 1];
/** At or above this tier the term is in the TITLE, which the row already highlights. */
export const MATCH_TIER_NAME_FLOOR = 4;

export interface Why {
  kind: MatchKind;
  /** `key` · `env var` · `matched` · `context` · `similar` */
  label: string;
  /** The token that caused the hit. EMPTY for a fuzzy match — there isn't one. */
  value: string;
}

/**
 * Where did this hit actually come from? Read straight off the server's `tier`.
 *
 * The cases are not decoration — the query has three OR'd branches (FTS phrase,
 * ILIKE substring, trigram `<%`), so a row can legitimately match with the term
 * appearing nowhere in it. `MONGO_SCIO` returns `scio--beta3-MONGO_OPLOG_URL`
 * that way: 44 of its 54 secret hits are trigram near-misses, not literal — a
 * measured figure, see docs/global-search.md.
 *
 * That case is exactly the one the handoff's rule is about — "always show why a
 * row matched when the match was not in the title" — because a row with no
 * highlight and no chip reads as arbitrary, and the honest answer is "this is a
 * near-miss, not an exact one". It must NOT be filed under `context`: that label
 * claims the subtitle matched, which for these rows is false.
 *
 * This used to re-derive the answer client-side by testing the term against each
 * field. That was a second implementation of the server's ranking rule, and the two
 * disagreed in the visible way described on `Hit.tier`. Now there is one rule, and
 * the label is the sort key.
 */
export function matchKind(h: Hit, _term?: string): MatchKind {
  if (h.tier >= MATCH_TIER_NAME_FLOOR) return 'name'; // 6 exact · 5 whole-word · 4 mid-token
  if (h.tier === 3) return 'body';
  if (h.tier === 2) return 'context';
  return 'similar'; // 1 token-only, 0 trigram near-miss
}

/**
 * The chip that explains a non-obvious hit. Null only for a title match — that is
 * already highlighted, so repeating it would be noise.
 */
export function whyFor(h: Hit, term: string): Why | null {
  const kind = matchKind(h, term);
  if (kind === 'name') return null;
  if (kind === 'body') {
    const label = h.entityType === 'secret' ? 'key' : h.entityType === 'service' ? 'env var' : 'matched';
    return { kind, label, value: h.matchedBody.join(', ') };
  }
  if (kind === 'context') return { kind, label: 'context', value: h.subtitle ?? '' };
  // Tier 1 and tier 0 both land here, and they are genuinely different: tier 1
  // satisfied the full-text phrase (the words are present, just not contiguously),
  // tier 0 satisfied nothing literal and only cleared the trigram threshold. Saying
  // "similar" for both understates the first and overstates the second.
  // No token to show either way — the value is deliberately empty and the chip
  // renders as a bare label rather than inventing a fragment that isn't in the row.
  return { kind, label: h.tier === 1 ? 'token match' : 'similar', value: '' };
}

// ── withheld ─────────────────────────────────────────────────────────────────

/**
 * "N secret matches are withheld" — the security-correct disclosure.
 *
 * The resolver filters secret hits against the caller's `allowedLevels` and drops
 * them silently. That made a level-gated secret indistinguishable from one that
 * doesn't exist, which is the wrong thing to tell someone about a secret: it sends
 * them looking for a typo instead of asking for access. The existence of matches
 * is not secret; the names are.
 */
export function WithheldBanner({ n, levels }: { n: number; levels?: string[] }) {
  if (n <= 0) return null;
  return (
    <div className="search-withheld-banner">
      <span className="search-withheld-banner__icon" aria-hidden="true">
        {/* padlock — the app's icon set calls it `access` */}
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
          <rect x="3.2" y="7" width="9.6" height="6.6" rx="1.6" />
          <path d="M5.6 7V5.2a2.4 2.4 0 014.8 0V7" />
        </svg>
      </span>
      {/* The handoff's wording was "above your L3 visibility", which reads cleanly
          because L3 is a rank. This app's levels are WORDS (critical / restricted /
          normal), and "above your normal visibility" parses as "above your usual
          visibility" — the opposite of a level statement. Naming the level in
          parentheses keeps it unambiguous. */}
      <span className="search-withheld-banner__text">
        {n} secret {n === 1 ? 'match is' : 'matches are'} withheld — more sensitive than your
        access level{levels?.length ? ` (${levels.join(' + ')})` : ''}. The name is hidden, not the
        fact that it exists.
      </span>
      <Link className="search-withheld-banner__cta" to="/access/new?type=secret">
        Request access
      </Link>
    </div>
  );
}
